"use client";

import { ButtonDark, ButtonTransparent, LongButton } from "@/stories/Button.stories";
import { CardSm } from "@/stories/Card.stories";
import { Pagination } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { usePathname, useRouter } from "next/navigation";
import { useState } from "react";
import { z } from "zod";
import type { Wrapper } from "../../../BlogTypes";
import Button from "../Cards/Button";
import Card from "../Cards/Card";
import Newsletter from "../Cards/Newsletter";
import ErrorModal from "../ErrorModal";
import { TextInput } from "../Inputs/TextInput";
import InfoModal from "../Modal";
import Text from "../Text/Text";
import Triangles from "../Triangles";
import CategoryMenu from "../CategoryMenu";
import NewBlogCard from "../Cards/NewBlogCard";
import Link from "next/link";
import CardSlider from "../Carousel/CardSlider";
import slugify from "slugify";
import { newestCategories } from "@/utils";

interface BlogsPageLayoutPrpos {
    blogs: Wrapper;
    onActivePageChange?: (activePage: number) => void;
    sendEmail: (subject: string, data: object) => Promise<void>;
    total: number;
    newBlogs?: Wrapper;
    category?: string;
    subcategory?: string;
    isSubCategory?: boolean;
}
const smallCardContent = [
    {
        title: "Fernbehandlung",
        img: "/images/icon2.svg",
        color: "bg-pink",
        url: "/de/fernbehandlung/jetzt-buchen",
    },
    {
        title: "Masterclass",
        img: "/images/icon4.svg",
        color: "bg-turquoise",
        url: "/de/masterclass",
    },
    {
        title: "Forschung",
        img: "/images/icon3.svg",
        color: "bg-blue",
        url: "/de/meine-forschung/forschungsergebnisse",
    },
    {
        title: "Einzelgespräch",
        img: "/images/icon1.svg",
        color: "bg-yellow",
        url: "/de/praxis/praxistermin",
    },
];
const tags = [
    {
        name: "Hochsensibilität",
        number: "5",
    },
    {
        name: "Trauma",
        number: "11",
    },
    {
        name: "Unsicherheiten",
        number: "5",
    },
    {
        name: "Depression",
        number: "20",
    },
    {
        name: "Überforderung",
        number: "12",
    },
    {
        name: "Blockaden",
        number: "20",
    },
    {
        name: "Emotionale Probleme",
        number: "2",
    },
    {
        name: "Traurigkeit",
        number: "20",
    },
    {
        name: "Innere Unruhe",
        number: "20",
    },
    {
        name: "Regeneratio",
        number: "20",
    },
    {
        name: "Stress",
        number: "20",
    },
    {
        name: "Überforderung",
        number: "20",
    },
    {
        name: "Burnout",
        number: "20",
    },
    {
        name: "Unsicherheiten",
        number: "20",
    },
];
export default function BlogsPageLayout({
    blogs,
    onActivePageChange,
    sendEmail,
    total,
    newBlogs,
    category,
    subcategory,
    isSubCategory,
}: BlogsPageLayoutPrpos) {
    const [activePage, setPage] = useState(1);
    const [error, setError] = useState(false);
    const pathname = usePathname();

    const changeActivePage = (page: number) => {
        setPage(page);
        const url = new URL(window.location.href);
        url.searchParams.set("page", activePage.toString());
        router.push(`${pathname}?page=${page}`);
    };
    const totalPages = Math.ceil(total / 8);
    const router = useRouter();
    const [openModal, setopenModal] = useState(false);
    const pills = tags.map((tag) => (
        <div
            key={tag.name}
            className="bg-white hover:bg-zinc-100 p-1 max-w-fit border border-blue-300 px-2 py-1 rounded-full text-center text-black text-xl1 font-normal leading-[34px]"
        >
            {tag.name}{" "}
            <span className="px-1.5 py-1 bg-zinc-100  rounded flex-col justify-start items-start gap-1 inline-flex">
                {tag.number}
            </span>
        </div>
    ));
    const shcema = z.object({
        email: z
            .string()
            .email()
            .refine((value) => value.length > 0, {
                message: "Bitte geben Sie eine gültige Email-Adresse ein",
            }),
        topic: z.string(),
    });
    const form = useForm({
        initialValues: {
            email: "",
            topic: "",
        },
        validate: zodResolver(shcema),
    });
    return (
        <>
            <div className="container mx-auto max-w-100xl py-20  space-y-4 lg:space-y-5">
                <div className="lg:flex w-full lg:justify-between pt-10 lg:space-x-64">
                    <div>
                        <div className="flex lg:text-center px-3 lg:px-0 justify-center">
                            <div className="lg:flex lg:w-[910px] space-x-16 justify-between">
                                <div className="text-black text-4xl lg:text-5xl font-bold font-['Baton Turbo Web'] leading-14">
                                    {pathname === "/de/alle-blogs" ? (
                                        <>Alle Blogkategorien</>
                                    ) : (
                                        <>
                                            {
                                                newestCategories.find(
                                                    (item) =>
                                                        item.category.slug === category,
                                                )?.category.name
                                            }{" "}
                                            {isSubCategory && "/"}{" "}
                                            {
                                                newestCategories
                                                    .find(
                                                        (item) =>
                                                            item.category.slug ===
                                                            category,
                                                    )
                                                    ?.subCategories.find(
                                                        (sub) =>
                                                            sub.slug === subcategory,
                                                    )?.name
                                            }
                                        </>
                                    )}
                                </div>
                                <div className="flex justify-center">
                                    <CategoryMenu />
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col mx-4 pt-6 justify-center mb-10 items-center space-y-6 ">
                            {blogs?.items.slice(0, 2).map((card) => (
                                <NewBlogCard
                                    category={card.category}
                                    subcategory={card.subcategories[0]}
                                    img={card.cover_media?.public_url}
                                    title={card.title}
                                    slug={card.slug}
                                    key={card.slug}
                                    categorySlug={card.category_slug}
                                    subcategorySlug={card.subcategories_slug}
                                    blog={card}
                                />
                            ))}
                        </div>

                        <div className="flex flex-col mx-4 pt-6 justify-center mb-10 items-center space-y-6 ">
                            {blogs?.items.slice(2, 4).map((card) => (
                                <NewBlogCard
                                    category={card.category}
                                    subcategory={card.subcategories[0]}
                                    img={card.cover_media?.public_url}
                                    title={card.title}
                                    slug={card.slug}
                                    key={card.slug}
                                    categorySlug={card.category_slug}
                                    subcategorySlug={card.subcategories_slug}
                                    blog={card}
                                />
                            ))}
                        </div>
                    </div>
                    <div className="w-full space-y-3 flex flex-col px-4 pt-6 lg:justify-start lg:items-start justify-center mb-10 items-center">
                        <div className=" text-neutral-800 text-xl1 font-bold  leading-8.5">
                            Neueste Beiträge
                        </div>
                        <ul className="space-y-4 w-11/12 lg:w-10/12 ">
                            {newBlogs
                                ? newBlogs.items.slice(0, 10).map((blog) => (
                                      <li
                                          key={blog.id}
                                          className="list-disc text-neutral-800 text-xl1 font-normal underline leading-7 lg:leading-8.5"
                                      >
                                          <a
                                              href={`/de/${slugify(blog.category, {
                                                  lower: true,
                                              })}/${slugify(blog.subcategories[0], {
                                                  lower: true,
                                              })}/${blog.slug}`}
                                          >
                                              {blog.title}
                                          </a>
                                      </li>
                                  ))
                                : blogs.items.slice(0, 10).map((blog) => (
                                      <li
                                          key={blog.id}
                                          className="list-disc text-neutral-800 text-xl1 font-normal underline leading-7 lg:leading-8.5"
                                      >
                                          <a
                                              href={`/de/${slugify(blog.category, {
                                                  lower: true,
                                              })}/${slugify(blog.subcategories[0], {
                                                  lower: true,
                                              })}/${blog.slug}`}
                                          >
                                              {blog.title}
                                          </a>
                                      </li>
                                  ))}
                        </ul>
                    </div>
                </div>
                <div className="rounded-lg w-full border py-10 border-zinc-300 justify-center items-start gap-6 hidden lg:inline-flex">
                    <div className="flex gap-10 ">
                        {smallCardContent.map((card) => (
                            <Link href={card.url} key={card.url}>
                                <Card
                                    key={card.title}
                                    imageHeight={80}
                                    imageWidth={80}
                                    button={
                                        <Button
                                            backgroundColor={card.color}
                                            textColor={ButtonTransparent.args?.textColor}
                                            border={ButtonTransparent.args?.border}
                                            as="a"
                                            href={card.url}
                                        />
                                    }
                                    buttonPlacement={CardSm.args?.buttonPlacement}
                                    width={"w-[280px]"}
                                    height={"h-[156px]"}
                                    imageSize={CardSm.args?.imageSize}
                                    imgSrc={card.img}
                                    title={card.title}
                                    backgroundColor={card.color}
                                    titleSize={CardSm.args?.titleSize}
                                    hasButton={false}
                                    titlePlacement="text-center"
                                    spacing={CardSm.args?.spacing}
                                    hasList={false}
                                    border=""
                                />
                            </Link>
                        ))}
                    </div>
                </div>
                <div className="md:hidden w-[17rem] lg:hidden container px-5">
                    <CardSlider slideContent={smallCardContent} />
                </div>
                {blogs.items.length > 4 && (
                    <div className="lg:flex w-full  justify-between pt-10 lg:space-x-64">
                        <div>
                            <div className="flex flex-col mx-4 justify-center mb-10 items-center space-y-6 ">
                                {blogs?.items.slice(5, 8).map((card) => (
                                    <NewBlogCard
                                        category={card.category}
                                        subcategory={card.subcategories[0]}
                                        img={card.cover_media?.public_url}
                                        title={card.title}
                                        slug={card.slug}
                                        key={card.slug}
                                        blog={card}
                                    />
                                ))}
                            </div>
                            <div className="flex justify-end">
                                <Pagination
                                    value={activePage}
                                    autoContrast={true}
                                    onChange={changeActivePage}
                                    total={totalPages}
                                    color="cyan"
                                />
                            </div>
                        </div>
                        <div className="w-full px-4 lg:px-0 space-y-3">
                            <div className=" text-neutral-800 text-xl1 font-bold  leading-8.5">
                                Schlagwörter
                            </div>
                            <div className="grid grid-cols-2 gap-2">{pills}</div>
                        </div>
                    </div>
                )}
            </div>

            <Triangles backgroundColor="bg-yellow -pb-[0.1rem]" buttomToTop={true} />
            <InfoModal
                size="xl"
                opened={openModal}
                onClose={() => {
                    setopenModal(false);
                }}
            >
                <div>
                    <Text
                        hasPretitle={false}
                        hasSubTitle={false}
                        hasText={true}
                        title="Vielen Dank für deine Blog-Idee!"
                        paragraphs={[
                            "Ich melde mich so schnell wie möglich bei dir.",
                            "Dein Thema werde ich gerne in die Videoproduktion aufnehmen. Bitte beachte, dass es zu Verzögerungen bei der Veröffentlichung kommen kann.",
                            "Ich melde mich wieder bei dir, sobald der Blogeitrag veröffentlich wurde. ",
                            "Liebe Grüsse",
                            "Rico Brunner",
                        ]}
                        paddings=""
                    />
                    <div className="flex py-5 justify-end">
                        <Button
                            hasIcon={ButtonDark.args?.hasIcon}
                            backgroundColor={ButtonDark.args?.backgroundColor}
                            size={ButtonDark.args?.size}
                            text="Home"
                            textColor={ButtonDark.args?.textColor}
                            textSize={ButtonDark.args?.textSize}
                            as="a"
                            href="/"
                        />
                    </div>
                </div>
            </InfoModal>

            <div className="bg-yellow flex justify-center">
                <div className="container ">
                    <Newsletter
                        hasTextArea={true}
                        hasForm={true}
                        hasCheckbox={false}
                        inputId="email"
                        buttonText="Blogthema jetzt senden"
                        backgroundColor="bg-yellow"
                        buttonPaddings="py-4 px-7"
                        hasSubText={false}
                        form={
                            <>
                                <form
                                    onSubmit={form.onSubmit((values) => {
                                        const data = {
                                            email: values.email,
                                            topic: values.topic,
                                        };
                                        sendEmail("Blogthema", data)
                                            .then(() => {
                                                setopenModal(true);
                                            })
                                            .catch(() => {
                                                <ErrorModal
                                                    open={error}
                                                    onClose={() => setError(false)}
                                                />;
                                            });
                                    })}
                                >
                                    <TextInput
                                        type="email"
                                        id="email"
                                        name="email"
                                        label="Deine E-Mail-Adresse"
                                        width="lg:w-120 md:w-120 w-83"
                                        backgroundColor="bg-yellow"
                                        {...form.getInputProps("email")}
                                    />
                                    <TextInput
                                        type="textarea"
                                        id="topic"
                                        name="topic"
                                        label="Thema senden"
                                        width="lg:w-120 md:w-120 w-83"
                                        backgroundColor="bg-yellow"
                                        {...form.getInputProps("topic")}
                                    />
                                    <div className="flex justify-center py-5">
                                        <Button
                                            backgroundColor={
                                                LongButton.args?.backgroundColor
                                            }
                                            textColor={LongButton.args?.textColor}
                                            text={"Blogthema jetzt senden"}
                                            textSize={LongButton.args?.textSize}
                                            paddings={"py-4 px-10"}
                                            rounded={LongButton.args?.rounded}
                                            size={LongButton.args?.size}
                                            border={LongButton.args?.border}
                                            buttonType="submit"
                                        />
                                    </div>
                                </form>
                            </>
                        }
                    />
                </div>
            </div>
            <div className="bg-yellow -mb-[0.04rem]">
                <Triangles backgroundColor="bg-darkGray" buttomToTop={false} />
            </div>
        </>
    );
}
